import { Component, computed, effect, inject, Input, Signal, signal, viewChild, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ClientListComponent } from '../matwins-manager/client/client-list/client-list.component';
import { DockListComponent } from '../matwins-tenant/dock/dock-list/dock-list.component';
import { PointOfServiceListComponent } from '../matwins-tenant/point-of-service/pos-list/point-of-service-list.component';
import { TruckerActivityListComponent } from '../matwins-tenant/trucker-activity/activity-list/trucker-activity-list.component';
import { TruckerListComponent } from '../matwins-tenant/trucker/trucker-list/trucker-list.component';
import { Client } from '@app/common-codes/models/matwins-manager/Client.model';
import { PointOfService } from '@app/common-codes/models/matwins-tenant/PointOfService.model';
import { FeatureMediatorComponent } from '@app/common-codes/components/feature-med-components/FeatureMediatorComponent';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { DependencyInjectionProvider } from '@app/common-codes/services/DependecyInjection.provider';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { LookupService } from '@app/common-codes/services/LookupService';
import { TenantAccountListComponent } from '../matwins-tenant/account/account-list/account-list.component';
import { EmployeeListComponent } from '../matwins-tenant/employee/employee-list/employee-list.component';
import { PermissionsService } from '@app/common-codes/services/Permissions.service';
import { ClientStackComponent } from '../matwins-manager/client/client-stack/client-stack.component';
import { EntityChangeLogService } from '@app/common-codes/services/EntityChangeLogService';
import { FilterStateService } from '@app/common-codes/services/FilterStateService';
import { StructuredDataRequest } from '@app/common-codes/_DTOs/IStructuredData';
import { Account } from '@app/common-codes/models/matwins-tenant/Account.model';
import { Employee } from '@app/common-codes/models/matwins-tenant/Employee.model';
import { Dock } from '@app/common-codes/models/matwins-tenant/Dock.model';
import { Trucker } from '@app/common-codes/models/matwins-tenant/Trucker.model';
import { TruckerActivity } from '@app/common-codes/models/matwins-tenant/TruckerActivity.model';

@Component({
  selector: 'app-trucker-feature',
  standalone: true,
  imports: [
    FeatureMediatorComponent,
    ClientListComponent,
    ClientStackComponent,
    PointOfServiceListComponent,
    TruckerActivityListComponent,
    TruckerListComponent,
    DockListComponent,
    TenantAccountListComponent,
    EmployeeListComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './trucker-feature.component.html'
})
export class TruckerFeatureComponent {
  @Input() shouldFetchLookupsOnRootSelect: boolean = false;

  @ViewChild(FeatureMediatorComponent) private mediator!: FeatureMediatorComponent<Client, PointOfService>;

  private clientList = viewChild(ClientListComponent);

  // Portee de la remise a zero des filtres, lue par l'en-tete de l'application.
  ngAfterViewInit(): void {
    this.filterStateService.activeListKeys.set(this.mediator.nestedListKeys());
    void this.restoreSavedSelection();
  }

  private changeLog = inject(EntityChangeLogService);
  private di = inject(DependencyInjectionProvider);
  protected filterStateService = inject(FilterStateService);


  public readonly nestedListTypes: (any)[] = [
    PointOfServiceListComponent,
    TruckerActivityListComponent,
    TruckerListComponent,
    DockListComponent,
    TenantAccountListComponent,
    EmployeeListComponent
  ];


  private _selectedClient = signal<Client | null>(null);


  readonly selectedClient: Signal<Client | null> = computed(() => this._selectedClient());

  readonly pointOfServices: Signal<PointOfService[]> = computed(() => {
    const client = this._selectedClient();
    return client?.relatedTenant?.pointOfServices ?? [];
  });

  readonly selectedPOS: Signal<PointOfService | null> = computed(() => {
    const posId = this.mediator.selectedLevel1Id();
    queueMicrotask(() => this.varsService.setPosId(posId));
    if (!posId) return null;
    return this.pointOfServices().find(p => p.id === posId) ?? null;
  });

  private uniqueById<T extends { id: any }>(items: T[]): T[] {
    const seen = new Set();
    return items.filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  readonly truckerActivities: Signal<TruckerActivity[]> = computed(() => {
    const pos = this.selectedPOS();
    if (pos) return pos.truckerActivities ?? [];
    const allPos = this.pointOfServices();
    const all = allPos.flatMap(p => p.truckerActivities ?? []);
    return this.uniqueById(all);
  });

  readonly truckers: Signal<Trucker[]> = computed(() => {
    const pos = this.selectedPOS();
    if (pos) return pos.truckers ?? [];
    const allPos = this.pointOfServices();
    const all = allPos.flatMap(p => p.truckers ?? []);
    return this.uniqueById(all);
  });

  readonly docks: Signal<Dock[]> = computed(() => {
    const pos = this.selectedPOS();
    if (pos) return pos.docks ?? [];
    const allPos = this.pointOfServices();
    const all = allPos.flatMap(p => p.docks ?? []);
    return this.uniqueById(all);
  });

  readonly employees: Signal<Employee[]> = computed(() => {
    let employees = this._selectedClient()?.unattachedEmployees ?? [];
    const pos = this.selectedPOS();
    if (pos) {
      let unionEmpl = [...pos.employees ?? []];
      return this.uniqueById(unionEmpl);
    }
    const allPos = this.pointOfServices();
    const all = [...employees, ...allPos.flatMap(p => p.employees ?? [])];
    return this.uniqueById(all);
  });

  readonly accounts: Signal<Account[]> = computed(() => {
    let accounts = this._selectedClient()?.unattachedAccounts ?? [];
    const pos = this.selectedPOS();
    if (pos) return pos.accounts ?? [];
    const allPos = this.pointOfServices();
    const all = [...accounts, ...allPos.flatMap(p => p.accounts ?? [])] as Account[];
    return this.uniqueById(all);
  });

  public getPointOfServices = (client: Client) => client?.relatedTenant?.pointOfServices ?? [];

  public level1Providers = {
    truckerActivities: (pos: PointOfService) => pos.truckerActivities ?? [],
    truckers: (pos: PointOfService) => pos.truckers ?? [],
    docks: (pos: PointOfService) => pos.docks ?? [],
    accounts: (pos: PointOfService) => pos.accounts ?? [],
    employees: (pos: PointOfService) => pos.employees ?? []
  };

  constructor(
    private tenantService: CurrentTenantService,
    private varsService: MatwinsVariablesService,
    private permissionsService: PermissionsService
  ) {
    effect(() => {
      const cleared = this.changeLog.lastClearedAt();
      if (cleared) {
        this.reloadClientRoot();
      }
    });
  }

  async reloadClientRoot(): Promise<void> {
    const tenantId = this.varsService.clientId;
    if (tenantId) {
      this.changeLog.clear(tenantId);
    }

    const clientId = this.mediator.selectedRootId();
    if (!clientId) return;

    const clientDataService = this.di.getOrCreateDataService(Client);
    const request: StructuredDataRequest = {
      pageNumber: 1,
      pageSize: 100,
      search: '',
      sortBy: '',
      sortOrder: 'asc',
      requiredLookups: [],
      dateRange: [],
      featureContext: 'trucker',
      includedEntities: this.mediator?.entityNames() ?? [],
      // Les filtres restaures voyagent avec la requete primaire : le premier affichage
      // porte deja les filtres au lieu de les voir s'appliquer apres coup.
      filters: this.filterStateService.filtersFor(this.mediator?.nestedListKeys() ?? [])
    };

    try {
      const response = await clientDataService.getAll(request);
      // Mettre à jour le client sélectionné avec les données fraîches
      const freshClient = response.items.find((c: Client) => c.id === clientId);
      if (freshClient) {
        this._selectedClient.set(freshClient);
      }

      this.mediator.needRefreshData.set({
        entityName: 'Client',
        columnName: '',
        filterType: 'refresh',
        value: {}
      });
    } catch (err) {
      console.error('[TruckerFeature] Erreur rechargement root', err);
    }
  }


  private async restoreSavedSelection(): Promise<void> {
    const savedClientId = this.varsService.clientId;
    if (!savedClientId) return;

    const clientDataService = this.di.getOrCreateDataService(Client);
    const request: StructuredDataRequest = {
      pageNumber: 1,
      pageSize: 100,
      search: '',
      sortBy: '',
      sortOrder: 'asc',
      requiredLookups: [],
      dateRange: [],
      featureContext: 'trucker',
      includedEntities: this.mediator?.entityNames() ?? [],
      filters: this.filterStateService.filtersFor(this.mediator?.nestedListKeys() ?? [])
    };

    try {
      const response = await clientDataService.getAll(request);
      const client = response.items.find((c: Client) => c.id === savedClientId);
      if (!client) return;
      const currentRootId = this.mediator?.selectedRootId();
      if (currentRootId && currentRootId !== client.id) return;
      if (!currentRootId) {
        this.onClientSelected(client);
      }
      this.clientList()?.listView()?.selectedId.set(client.id);
    } catch (err) {
      console.error('[TruckerFeature] Erreur restauration du client memorise', err);
    }
  }


  onClientSelected(client: Client | null) {
    this._selectedClient.set(client);
    this.mediator.selectRoot(client);

    const posItems = client?.relatedTenant?.pointOfServices ?? [];
    const savedPosId = client ? this.varsService.getSavedPosId(client.id) : null;
    const targetPos = posItems.find(p => p.id === savedPosId) ?? posItems.find(p => p.defaultPOS) ?? null;
    this.mediator.selectLevel1(targetPos);

    this.tenantService.currentClientId.set(client?.id || '');
    this.varsService.setClientId(client?.id || null);
    LookupService.StoragePage = client?.relatedTenant?.prefix || 'all';
    this.tenantService.currentTenant.set(client?.relatedTenant ?? null);

    const prefix = client?.relatedTenant?.prefix ?? '';
    this.varsService.tenantPrefix = prefix.replace('https://', '').split('.')[0];
    this.varsService.tenantPrefixChanged$.set(prefix);

    if (!this.shouldFetchLookupsOnRootSelect && client !== null) {
      this.fetchLookupsExplicitly(client?.prefix);
    }
  }

  public async fetchLookupsExplicitly(clientDomain: string | undefined): Promise<void> {
    if (this.mediator) {
      const allRequiredLookups = this.mediator.otherLookupProfiles();
      if (allRequiredLookups.length > 0) {
        const posDataService = this.di.getOrCreateDataService(PointOfService);
        await posDataService.getLookupsOnly(allRequiredLookups);
        console.log('[TruckerFeature] Lookups fetched explicitly:', allRequiredLookups);
      }
    }
  }

  public get RequiredClientPermissions(): string[] {
    return [`Permissions.Entities.Manager.Client.Read`];
  }
  public get HasRequiredClientPermissions(): boolean {
    return this.permissionsService.UserHasAllPermissions(this.RequiredClientPermissions);
  }
  public get RequiredTenantPermissions(): string[] {
    return [`Permissions.Entities.Tenant.Tenant.Read`];
  }
  public get HasRequiredTenantPermissions(): boolean {
    return this.permissionsService.UserHasAllPermissions(this.RequiredTenantPermissions);
  }
}

